// From the type module rather than the `@data` barrel: this file is imported by a test that pulls in
// nothing else, and the barrel drags a cycle of app constants in behind it.
import { SprintProductEntity, SprintProductStatus } from '@data/utils/types';
import React, { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Whether this report should be marked as carrying a comment *this reader* has not read.
 *
 * The whole rule is the server's — whose queue the comment is in, and whether they have read it — but
 * every surface still asks through here, so the dot, the filter and the badge cannot drift apart, and a
 * report the browser has patched optimistically answers the same way as one straight off the wire.
 */
export const hasUnreadComment = (sprintProduct?: Partial<SprintProductEntity>): boolean =>
  !!sprintProduct?.viewerHasUnreadComment;

interface RemarkFields {
  status?: SprintProductStatus;
  disputedAt?: Date | null;
  comment?: string;
  disputeResolvedAt?: Date | null;
}

/** A remark was written on this report, answered or not. It stays on the report once it is answered. */
const hasRemark = (sprintProduct?: RemarkFields): boolean => !!sprintProduct?.disputedAt && !!sprintProduct.comment;

/**
 * Whether a dispute is where this report currently stands. The status is part of the answer: a report
 * disputed and then approved keeps the record of the dispute, but the approval is what stands, so it is
 * the approval that gets read back.
 *
 * So is the answer we gave it. `disputeResolvedAt` is what settles a remark — it is not deleted — so a
 * report carrying an answered remark is not waiting on anybody.
 */
export const hasDispute = (sprintProduct?: RemarkFields): boolean =>
  sprintProduct?.status === SprintProductStatus.closed && hasRemark(sprintProduct) && !sprintProduct.disputeResolvedAt;

/**
 * Whether we have this report open to answer a remark on it. The remark is still on the desk — reopening
 * does not settle anything — but the report is ours to work on rather than theirs to answer, so it reads
 * as open on our side and closed on theirs.
 */
export const isAnsweringRemark = (sprintProduct?: RemarkFields): boolean =>
  sprintProduct?.status === SprintProductStatus.open && hasRemark(sprintProduct) && !sprintProduct.disputeResolvedAt;

/** A remark this report carries that has been answered: read as what was asked, not as work waiting. */
export const hasSettledRemark = (sprintProduct?: RemarkFields): boolean =>
  hasRemark(sprintProduct) && !!sprintProduct?.disputeResolvedAt;

/**
 * A remark still waiting for an answer, whichever side of the desk the report is on: theirs to answer while
 * it is closed, ours while we have it open. Reopening a disputed report moves it between the two without
 * settling anything, so this is what "there is a dispute on this report" means.
 */
export const hasStandingRemark = (sprintProduct?: RemarkFields): boolean =>
  hasDispute(sprintProduct) || isAnsweringRemark(sprintProduct);

/** Not a stored status — a disputed report keeps the status underneath — but it is one the reader can see. */
export const DISPUTED_STATUS = 'disputed';

export type ReportStatus = SprintProductStatus | typeof DISPUTED_STATUS;

/**
 * The statuses a report reads as. Usually just the one it stores, but a standing remark is shown alongside
 * it rather than instead of it: a reopened dispute is Open and Disputed at once, and dropping either half
 * loses where the report stands or why. The pills and the status filter both ask through here, so a report
 * cannot read as one thing and be filtered as another.
 */
export const reportStatusesOf = (sprintProduct?: RemarkFields): ReportStatus[] => {
  const statuses: ReportStatus[] = [sprintProduct?.status ?? SprintProductStatus.open];
  if (hasStandingRemark(sprintProduct)) statuses.push(DISPUTED_STATUS);
  return statuses;
};

/**
 * A client left a comment nobody on our side has read. Sits to the left of whatever it marks, matching
 * the unreported-day dot on the Reporting page, so the two read as the same kind of signal.
 *
 * Colour alone is not a signal for everyone, so the dot itself is decorative and the meaning is carried
 * by text only a screen reader hears.
 */
export const UnreadCommentDot = (): ReactElement => {
  const { t } = useTranslation();

  return (
    <>
      <span aria-hidden className="size-2 shrink-0 rounded-full bg-destructive" />
      <span className="sr-only">{t('unreadComment')}</span>
    </>
  );
};
